import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

/**
 * The pan and zoom shared by the knowledge graph and the Idea Board (docs/PARITY-STEP-4-PLAN.md
 * decision 2), taken out of the graph's canvas: drag the background to pan, the wheel or a
 * pinch to zoom about the pointer, and the keys (+ and − to zoom, 0 to fit, the arrows to
 * move). `view` maps drawing units to the screen: screen = view.x + point × view.scale.
 */

export interface View {
  readonly scale: number;
  readonly x: number;
  readonly y: number;
}

export interface PanZoom {
  readonly box: React.RefObject<HTMLDivElement | null>;
  readonly svg: React.RefObject<SVGSVGElement | null>;
  /** The drawing's size on screen. */
  readonly size: { readonly width: number; readonly height: number };
  readonly view: View;
  readonly setView: React.Dispatch<React.SetStateAction<View>>;
  readonly zoomAt: (px: number, py: number, factor: number) => void;
  /** Whether the pointer moved enough since it went down to be a drag, not a click. */
  readonly dragged: React.RefObject<boolean>;
  /** A point on screen (client coordinates) in drawing units. */
  readonly toDrawing: (clientX: number, clientY: number) => { x: number; y: number };
  /** For the drawing's `<svg>`. */
  readonly handlers: {
    readonly onPointerDown: (event: PointerEvent<SVGSVGElement>) => void;
    readonly onPointerMove: (event: PointerEvent<SVGSVGElement>) => void;
    readonly onPointerUp: (event: PointerEvent<SVGSVGElement>) => void;
    readonly onPointerCancel: (event: PointerEvent<SVGSVGElement>) => void;
  };
  /** The drawing's keys; `fit` is what 0 does. */
  readonly onKeyDown: (event: KeyboardEvent, fit: () => void) => void;
}

export function usePanZoom(minScale: number, maxScale: number, initial: View = { scale: 1, x: 0, y: 0 }): PanZoom {
  const box = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ width: 800, height: 560 });
  const [view, setView] = useState<View>(initial);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragged = useRef(false);
  const clamp = useCallback((s: number) => Math.min(maxScale, Math.max(minScale, s)), [minScale, maxScale]);

  // The drawing's size follows its box.
  useLayoutEffect(() => {
    const element = box.current!;
    const resize = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const zoomAt = useCallback(
    (px: number, py: number, factor: number) => {
      setView((v) => {
        const scale = clamp(v.scale * factor);
        const k = scale / v.scale;
        return { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
      });
    },
    [clamp],
  );

  // The wheel zooms towards the pointer; it needs a listener that may stop the page scrolling.
  useEffect(() => {
    const element = svg.current!;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      zoomAt(event.clientX - rect.left, event.clientY - rect.top, Math.exp(-event.deltaY * 0.0015));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const local = (event: { clientX: number; clientY: number }) => {
    const rect = svg.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    pointers.current.set(event.pointerId, local(event));
    dragged.current = false;
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const before = pointers.current.get(event.pointerId);
    if (!before) return;
    const now = local(event);
    if (pointers.current.size === 1) {
      const dx = now.x - before.x;
      const dy = now.y - before.y;
      if (!dragged.current && Math.abs(dx) + Math.abs(dy) > 2) {
        // Captured only once it's a drag: capturing at once would turn a click on something into a click on the drawing.
        dragged.current = true;
        svg.current!.setPointerCapture(event.pointerId);
      }
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    } else if (pointers.current.size === 2) {
      // A pinch: zoom by how far apart the two fingers move, about their midpoint.
      const [other] = [...pointers.current].filter(([id]) => id !== event.pointerId).map(([, p]) => p);
      const was = Math.hypot(before.x - other!.x, before.y - other!.y);
      const is = Math.hypot(now.x - other!.x, now.y - other!.y);
      if (was > 0) zoomAt((now.x + other!.x) / 2, (now.y + other!.y) / 2, is / was);
      dragged.current = true;
    }
    pointers.current.set(event.pointerId, now);
  };

  const onPointerUp = (event: PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(event.pointerId);
  };

  const onKeyDown = (event: KeyboardEvent, fit: () => void) => {
    const centre = [size.width / 2, size.height / 2] as const;
    const step = 60;
    const moves: Record<string, () => void> = {
      '+': () => zoomAt(...centre, 1.25),
      '=': () => zoomAt(...centre, 1.25),
      '-': () => zoomAt(...centre, 0.8),
      '0': fit,
      ArrowLeft: () => setView((v) => ({ ...v, x: v.x + step })),
      ArrowRight: () => setView((v) => ({ ...v, x: v.x - step })),
      ArrowUp: () => setView((v) => ({ ...v, y: v.y + step })),
      ArrowDown: () => setView((v) => ({ ...v, y: v.y - step })),
    };
    const move = moves[event.key];
    if (move && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      move();
    }
  };

  const toDrawing = (clientX: number, clientY: number) => {
    const p = local({ clientX, clientY });
    return { x: (p.x - view.x) / view.scale, y: (p.y - view.y) / view.scale };
  };

  return { box, svg, size, view, setView, zoomAt, dragged, toDrawing, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }, onKeyDown };
}

/** The view that shows a box of drawing units whole, centred, between the scales given. */
export function fitView(bounds: { minX: number; minY: number; maxX: number; maxY: number }, size: { width: number; height: number }, margin: number, minScale: number, maxScale: number): View {
  const fits = (room: number, span: number) => (span > 0 ? Math.max(room - 2 * margin, 40) / span : maxScale);
  const scale = Math.min(maxScale, Math.max(minScale, Math.min(fits(size.width, bounds.maxX - bounds.minX), fits(size.height, bounds.maxY - bounds.minY))));
  return { scale, x: size.width / 2 - ((bounds.minX + bounds.maxX) / 2) * scale, y: size.height / 2 - ((bounds.minY + bounds.maxY) / 2) * scale };
}
